import { useEffect, useRef, useState, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  
  IconButton, 
  InputAdornment, 
  TextField,
  Chip,

  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
  Tooltip,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import ShareLocationIcon from '@mui/icons-material/ShareLocation';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PersonIcon from '@mui/icons-material/Person';

import PlaceIcon from '@mui/icons-material/Place';
import NotesIcon from '@mui/icons-material/Notes';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase } from '../lib/supabaseClient';
import UserIcon from '../components/UserIcon';
import TidioChat from '../components/TidioChat';
import { useLocationSharing, LocationSharingProvider } from '../context/LocationSharingContext';
import { useNavigate } from 'react-router-dom';
import EmergencyAlertTrigger from '../components/EmergencyAlertTrigger';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LayersIcon from '@mui/icons-material/Layers';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Removed unused STORAGE_KEY and PADANG_COORDS constants

const USER_GROUPS = [
  'OS Team',
  'Show Support',
  'Show Management (Bay)',
  'Show Management (Padang)',
  'Artiste Management',
  'Log & Finance',
  'Ops. Safety and Contingencies'
];

interface Location {
  id: string;
  user_id: string;
  latitude: number | null;
  longitude: number | null;
  last_updated: string;
  sharing_id: string;
  username: string;
  user_group: string;
  eta: string;
  shared_location: string;
  platform: string;
  device_id?: string;
  accuracy?: number;
  speed?: number;
  heading?: number;
  altitude?: number;
  source?: string;
  created_at: string;
  _deviceInstanceLabel?: string;
}

interface EmergencyAlert {
  id: string;
  user_id: string;
  latitude: number;
  longitude: number;
  address: string;
  status: string;
  created_at: string;
  username: string;
  user_group: string;
  notes?: string;
}

// Offline storage for location data
class OfflineLocationStorage {
  private static readonly STORAGE_KEY = 'offline_locations';
  private static readonly MAX_OFFLINE_ENTRIES = 1000;
  
  static saveLocation(location: Location): void {
    try {
      const existing = localStorage.getItem(this.STORAGE_KEY);
      const locations: Location[] = existing ? JSON.parse(existing) : [];
      
      // Add timestamp for offline tracking
      const offlineLocation = {
        ...location,
        offline_timestamp: new Date().toISOString(),
        is_offline: true
      };
      
      locations.push(offlineLocation);
      
      // Keep only recent entries
      if (locations.length > this.MAX_OFFLINE_ENTRIES) {
        locations.splice(0, locations.length - this.MAX_OFFLINE_ENTRIES);
      }
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(locations));
    } catch (error) {
      console.error('Error saving offline location:', error);
    }
  }
  
  static getOfflineLocations(): Location[] {
    try {
      const existing = localStorage.getItem(this.STORAGE_KEY);
      return existing ? JSON.parse(existing) : [];
    } catch (error) {
      console.error('Error getting offline locations:', error);
      return [];
    }
  }
  
  static clearOfflineLocations(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }
  
  static getOfflineStatus(): { count: number; hasData: boolean } {
    const locations = this.getOfflineLocations();
    return {
      count: locations.length,
      hasData: locations.length > 0
    };
  }
}

const LiveTrackingContent = () => {
  const navigate = useNavigate();
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMap = useRef<L.Map | null>(null);
  const markersLayer = useRef<L.LayerGroup | null>(null);
  const emergencyMarkersLayer = useRef<L.LayerGroup | null>(null);
  const markersMap = useRef<Map<string, L.Marker>>(new Map());
  const emergencyMarkersMap = useRef<Map<string, L.Marker>>(new Map());
  // Removed unused markers state
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<{ [key: string]: boolean }>(() => {
    const allGroups: { [key: string]: boolean } = {};
    USER_GROUPS.forEach(group => {
      allGroups[group] = true;
    });
    return allGroups;
  });
  const [sharedLocations, setSharedLocations] = useState<Location[]>([]);
  const [activeEmergencies, setActiveEmergencies] = useState<EmergencyAlert[]>([]);
  const [searchQuery] = useState("");
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
  const [selectedEmergency, setSelectedEmergency] = useState<EmergencyAlert | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);


  const [mapStyle, setMapStyle] = useState<'map' | 'satellite' | 'hybrid'>('map');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Removed: const open = Boolean(anchorEl);
  
  // Smart hybrid real-time system with intelligent polling fallback
  const [realtimeStatus, setRealtimeStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');
  const [lastRealtimeUpdate] = useState<number>(Date.now());
  const [isPollingActive, setIsPollingActive] = useState(false);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const {
    isSharing,
    startSharing,
    stopSharing,
    isBackgroundTracking
  } = useLocationSharing();





  // Map initialization - only once
  useEffect(() => {
    if (!mapRef.current) return;
    
    // Remove any existing map instance on this container
    if ((mapRef.current as any)._leaflet_id) {
      delete (mapRef.current as any)._leaflet_id;
    }
    
    // Only initialize if not already initialized
    if (leafletMap.current) return;

    leafletMap.current = L.map(mapRef.current).setView([1.3521, 103.8198], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(leafletMap.current);
    markersLayer.current = L.layerGroup().addTo(leafletMap.current);
    emergencyMarkersLayer.current = L.layerGroup().addTo(leafletMap.current);
    console.log('LiveTracking: Map and layers initialized:', !!leafletMap.current, !!markersLayer.current, !!emergencyMarkersLayer.current);
    
    return () => {
      if (leafletMap.current) {
        leafletMap.current.remove();
        leafletMap.current = null;
        markersLayer.current = null;
          emergencyMarkersLayer.current = null;
      }
    };
  }, []);

  // Subscribe to realtime only once
  useEffect(() => {
    const channel = supabase
      .channel('location_updates')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'shared_location' }, () => {
        fetchLocations(true); // Fetch latest locations on INSERT
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'shared_location' }, () => {
        fetchLocations(true); // Fetch latest locations on UPDATE
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'shared_location' }, () => {
        fetchLocations(true); // Fetch latest locations on DELETE
      })
      .subscribe();
    // Initial fetch
    (async () => {
      const { data, error } = await supabase
        .from('shared_location')
        .select('user_id, latitude, longitude, created_at')
        .order('created_at', { ascending: false })
        .limit(500);
      console.log('LiveTracking: Fetched data from Supabase:', data, 'Error:', error);
      if (!error && data) {
        // Removed unused markers processing
      }
    })();
    return () => {
      channel.unsubscribe();
    };
  }, []);

  // Network status handling
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      console.log('Network connection restored');
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      console.log('Network connection lost');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch user info and check admin status
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
          const { data: userData } = await supabase
          .from('users')
            .select('username, user_group')
          .eq('id', user.id)
          .single();
          
          if (userData) {
            console.log('User info loaded:', userData);
        }
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    fetchUserInfo();
  }, []);

  // Color-coded user group mapping for easy identification
  const getUserGroupColor = (userGroup: string): string => {
    const groupColors: { [key: string]: string } = {
      'OS Team': '#dc3545',                    // Red
      'Show Support': '#007bff',               // Blue
      'Show Management (Bay)': '#28a745',      // Green
      'Show Management (Padang)': '#17a2b8',   // Cyan
      'Artiste Management': '#ffc107',         // Yellow
      'Log & Finance': '#6f42c1',              // Purple
      'Ops. Safety and Contingencies': '#fd7e14' // Orange
    };
    
    return groupColors[userGroup] || '#6c757d'; // Default gray for unknown groups
  };

  // Create custom icon for markers with user group color coding
  const createCustomIcon = (username: string, userGroup: string) => {
    const color = getUserGroupColor(userGroup);
    const initials = username.split(' ').map(name => name.charAt(0)).join('').toUpperCase();
    
    return `
      <div style="
        width: 40px; 
        height: 40px; 
        background: ${color}; 
        border: 3px solid white; 
        border-radius: 50%; 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        color: white; 
        font-weight: bold; 
        font-size: 14px; 
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        text-transform: uppercase;
        position: relative;
      ">
        ${initials}
        <div style="
          position: absolute;
          bottom: -2px;
          right: -2px;
          width: 12px;
          height: 12px;
          background: ${color};
          border: 2px solid white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 8px;
          font-weight: bold;
          color: white;
        ">
          ${userGroup.charAt(0)}
        </div>
      </div>
    `;
  };

  // Add performance optimizations for 500+ users (removed redundant rapid toggling protection)
  const fetchLocationsRef = useRef<{ lastFetch: number; cache: Location[]; cacheTime: number }>({
    lastFetch: 0,
    cache: [],
    cacheTime: 0
  });

  // Smart caching for 500+ users - cache for 2 seconds to reduce database load
  const CACHE_DURATION = 2000; // 2 seconds
  const MAX_LOCATIONS_PER_FETCH = 1000; // Limit to prevent memory issues

  const fetchLocations = async (forceRefresh = false) => {
    try {
      const now = Date.now();
      
      // Use cache if it's still valid and not forcing refresh
      if (!forceRefresh && 
          fetchLocationsRef.current.cache.length > 0 && 
          now - fetchLocationsRef.current.cacheTime < CACHE_DURATION) {
        console.log('LiveTracking: Using cached locations:', fetchLocationsRef.current.cache.length);
        setSharedLocations(fetchLocationsRef.current.cache);
        return;
      }

      fetchLocationsRef.current.lastFetch = now;
      
      console.log('LiveTracking: Starting fetchLocations...');
      let locations: Location[] = [];
      
      if (isOnline) {
        console.log('LiveTracking: Fetching locations from database');
        
        // Optimized query for 500+ users with pagination and limits
        const { data, error } = await supabase
          .from('shared_location')
          .select('id, user_id, latitude, longitude, last_updated, sharing_id, username, user_group, eta, shared_location, platform, device_id, created_at')
          .not('latitude', 'is', null)
          .not('longitude', 'is', null)
          .neq('latitude', 0)
          .neq('longitude', 0)
          .order('last_updated', { ascending: false })
          .limit(MAX_LOCATIONS_PER_FETCH);

        if (error) {
          console.error('Error fetching locations:', error);
          return;
        }
        
        console.log('LiveTracking: Raw data from Supabase:', data?.length || 0, 'locations');
        console.log('LiveTracking: Sample location data:', data?.[0]);
        
        if (data && data.length > 0) {
          // Optimized processing for large datasets
          locations = data.map((item: any) => ({
            id: item.id || item.user_id,
            user_id: item.user_id,
            latitude: item.latitude,
            longitude: item.longitude,
            last_updated: item.last_updated || item.created_at,
            sharing_id: item.sharing_id || item.user_id,
            username: item.username || 'Unknown',
            user_group: item.user_group || 'Unknown',
            eta: item.eta || '',
            shared_location: item.shared_location || '',
            platform: item.platform || 'unknown',
            device_id: item.device_id,
            accuracy: undefined, // Not available in shared_location table
            speed: undefined, // Not available in shared_location table
            heading: undefined, // Not available in shared_location table
            altitude: undefined, // Not available in shared_location table
            source: undefined, // Not available in shared_location table
            created_at: item.created_at
          })) as Location[];
          
          console.log('LiveTracking: Processed locations:', locations.length);
        } else {
          console.log('LiveTracking: No data returned from Supabase');
        }
      } else {
        console.log('LiveTracking: Using offline data');
        locations = OfflineLocationStorage.getOfflineLocations();
      }
      
      // Update cache
      fetchLocationsRef.current.cache = locations;
      fetchLocationsRef.current.cacheTime = now;
      
      console.log('LiveTracking: Final locations to display:', locations.length);
      console.log('LiveTracking: Location details:', locations.map(loc => ({
        username: loc.username,
        platform: loc.platform,
        coords: [loc.latitude, loc.longitude],
        last_updated: loc.last_updated,
        user_id: loc.user_id,
        device_id: loc.device_id
      })));
      setSharedLocations(locations);
      
    } catch (error) {
      console.error('Error in fetchLocations:', error);
      
      // Fallback to offline data on error
      if (isOnline) {
        console.log('Falling back to offline data due to error');
        const offlineLocations = OfflineLocationStorage.getOfflineLocations();
        setSharedLocations(offlineLocations);
      }
    }
  };

  // Smart hybrid real-time system with intelligent polling fallback - optimized for 500+ users
  const startSmartPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    setIsPollingActive(true);
    console.log('LiveTracking: Starting smart polling fallback');
    
    // Adaptive polling based on user count - slower for more users to reduce server load
    const getPollingInterval = () => {
      const userCount = sharedLocations.length;
      if (userCount > 500) return 3000; // 3s for 500+ users
      if (userCount > 200) return 2500; // 2.5s for 200+ users
      if (userCount > 100) return 2000; // 2s for 100+ users
      return 1500; // 1.5s for <100 users
    };
    
    const pollWithAdaptiveInterval = () => {
      const interval = getPollingInterval();
      console.log(`LiveTracking: Executing fallback poll (${interval}ms interval) for ${sharedLocations.length} users`);
      fetchLocations();
      
      // Schedule next poll with adaptive interval
      pollingIntervalRef.current = setTimeout(pollWithAdaptiveInterval, interval);
    };
    
    // Start polling with adaptive interval
    pollWithAdaptiveInterval();
  }, [sharedLocations.length]);

  const stopSmartPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPollingActive(false);
    console.log('LiveTracking: Stopped smart polling');
  }, []);

  // Health check for real-time connection
  const startHealthCheck = useCallback(() => {
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
    }
    
    healthCheckIntervalRef.current = setInterval(() => {
      const timeSinceLastUpdate = Date.now() - lastRealtimeUpdate;
      
      // If no real-time updates for 5 seconds, mark as disconnected and start polling
      if (timeSinceLastUpdate > 5000 && realtimeStatus === 'connected') {
        console.log('LiveTracking: Real-time connection appears to be down, starting polling');
        setRealtimeStatus('disconnected');
        startSmartPolling();
      }
    }, 5000); // Health check every 5 seconds
  }, [lastRealtimeUpdate, realtimeStatus, startSmartPolling]);

  const stopHealthCheck = useCallback(() => {
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
      healthCheckIntervalRef.current = null;
    }
  }, []);

  // Initialize smart hybrid system
  useEffect(() => {
    // Start health check
    startHealthCheck();
    
    // Start polling immediately as fallback
    startSmartPolling();
    
    // Initial fetch with immediate retry for better responsiveness
    fetchLocations();
    
    // Immediate retry after 500ms to catch any missed updates
    const immediateRetry = setTimeout(() => {
      console.log('LiveTracking: Executing immediate retry fetch');
      fetchLocations();
    }, 500);
    
    return () => {
      stopHealthCheck();
      stopSmartPolling();
      clearTimeout(immediateRetry);
    };
  }, [startHealthCheck, startSmartPolling, stopHealthCheck, stopSmartPolling]);

  // Fetch locations when online status changes
  useEffect(() => {
    if (isOnline) {
      console.log('LiveTracking: Online status restored, fetching locations');
      fetchLocations();
    }
  }, [isOnline]);

  // Trigger immediate fetch when sharing starts to reduce lag
  useEffect(() => {
    if (isSharing) {
      console.log('Sharing started, triggering immediate location fetch');
      fetchLocations();
    }
  }, [isSharing]);

  // Add location update event optimization for 500+ users (removed redundant processing flags)
  const locationUpdateRef = useRef<{ 
    pendingUpdates: Array<CustomEvent>; 
    batchTimeout: NodeJS.Timeout | null;
  }>({
    pendingUpdates: [],
    batchTimeout: null
  });

  // Process batched location updates efficiently
  const processLocationUpdates = useCallback(() => {
    if (locationUpdateRef.current.pendingUpdates.length === 0) {
      return;
    }
    
    // Get all pending updates
    const updates = [...locationUpdateRef.current.pendingUpdates];
    locationUpdateRef.current.pendingUpdates = [];
    
    console.log('LiveTracking: Processing', updates.length, 'batched location updates');
    console.log('LiveTracking: Update details:', updates.map(u => ({ type: u.detail.type, data: u.detail.data })));
    
    // Determine if we need to force refresh based on update types
    const hasImmediateFetch = updates.some(update => update.detail.type === 'IMMEDIATE_FETCH');
    const hasInsertUpdate = updates.some(update => update.detail.type === 'INSERT' || update.detail.type === 'UPDATE');
    const hasDelete = updates.some(update => update.detail.type === 'DELETE');
    
    // Force refresh for immediate fetch or if we have multiple types of updates
    const shouldForceRefresh = hasImmediateFetch || hasInsertUpdate || hasDelete;
    
    console.log('LiveTracking: Force refresh needed:', shouldForceRefresh, 'due to:', {
      hasImmediateFetch,
      hasInsertUpdate,
      hasDelete
    });
    
    // Fetch locations with force refresh if needed
    fetchLocations(shouldForceRefresh);
    
    // Process any new updates that came in while we were processing
    if (locationUpdateRef.current.pendingUpdates.length > 0) {
      setTimeout(() => processLocationUpdates(), 50);
    }
  }, [fetchLocations]);

  // Listen for real-time updates from LocationSharingContext - optimized for 500+ users
  useEffect(() => {
    const handleLocationUpdate = (event: CustomEvent) => {
      console.log('LiveTracking: Real-time event received:', event.detail.type);
      
      // Skip processing if sharing has stopped to prevent glitch
      if (!isSharing && event.detail.type !== 'DELETE') {
        console.log('LiveTracking: Skipping location update - sharing has stopped');
        return;
      }
      
      // Add update to pending queue
      locationUpdateRef.current.pendingUpdates.push(event);
      
      // Limit queue size to prevent memory issues
      if (locationUpdateRef.current.pendingUpdates.length > 20) {
        locationUpdateRef.current.pendingUpdates = locationUpdateRef.current.pendingUpdates.slice(-10);
      }
      
      // Clear existing timeout
      if (locationUpdateRef.current.batchTimeout) {
        clearTimeout(locationUpdateRef.current.batchTimeout);
      }
      
      // Batch updates - process after 100ms of no new updates
      locationUpdateRef.current.batchTimeout = setTimeout(() => {
        processLocationUpdates();
      }, 100);
    };

    // Add event listener for real-time updates
    window.addEventListener('locationUpdate', handleLocationUpdate as EventListener);

    // Cleanup event listener
    return () => {
      window.removeEventListener('locationUpdate', handleLocationUpdate as EventListener);
      if (locationUpdateRef.current.batchTimeout) {
        clearTimeout(locationUpdateRef.current.batchTimeout);
      }
    };
  }, [processLocationUpdates, isSharing]);

  // Update filtered locations when shared locations change
  useEffect(() => {
    let filtered = sharedLocations;
    const anySelected = Object.values(selectedGroups).some(selected => selected);
    // If no groups are selected, show no users
    if (!anySelected) {
      filtered = [];
    } else {
      // Apply group filter as usual
      filtered = filtered.filter(location => {
        return Boolean(selectedGroups[location.user_group as keyof typeof selectedGroups] ?? false) || Boolean(selectedGroups['All'] ?? false);
      });
    }
    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(location =>
        location.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.user_group.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.shared_location.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    setFilteredLocations(filtered);
  }, [sharedLocations, selectedGroups, searchQuery]);

  // Add marker rendering optimization for 500+ users (removed redundant throttling)
  const markerUpdateRef = useRef<{ batchSize: number }>({
    batchSize: 50 // Process markers in batches of 50
  });

  // Update markers whenever filtered locations change - optimized for 500+ users
  useEffect(() => {
    if (!leafletMap.current || !markersLayer.current) return;
    
    console.log('LiveTracking: Updating markers for', filteredLocations.length, 'locations');
    
    // Clear any existing markers from the layer to prevent duplicates
    markersLayer.current.clearLayers();
    
    const locationsToProcess = [...filteredLocations];
    
    // Build a set of all marker keys present in the latest data
    const currentMarkerKeys = new Set<string>();
    
    // Process markers in batches for better performance with 500+ users
    const processMarkerBatch = (startIndex: number) => {
      const endIndex = Math.min(startIndex + markerUpdateRef.current.batchSize, locationsToProcess.length);
      const batch = locationsToProcess.slice(startIndex, endIndex);
      
      console.log('LiveTracking: Processing marker batch', startIndex, 'to', endIndex, 'of', locationsToProcess.length);
      
      batch.forEach(location => {
        const { latitude, longitude, username, user_group, eta, sharing_id, device_id, user_id, platform } = location;
        
        // Use consistent marker key format that matches the removal logic
        const markerKey = device_id && device_id.trim() !== ''
          ? `${user_id}_${platform}_${device_id}`
          : sharing_id && sharing_id.trim() !== ''
          ? `${user_id}_${platform}_${sharing_id}`
          : `${user_id}_${platform}`;
        
        currentMarkerKeys.add(markerKey);
        
        if (latitude === null || longitude === null || latitude === undefined || longitude === undefined) {
          console.log('LiveTracking: Skipping marker for invalid coordinates:', username, [latitude, longitude]);
          return;
        }
        if (latitude === 0 && longitude === 0) {
          console.log('LiveTracking: Skipping marker for zero coordinates:', username, [latitude, longitude]);
          return;
        }
        
        // Remove any existing marker with the same key to prevent duplicates
        const existingMarker = markersMap.current.get(markerKey);
        if (existingMarker) {
          existingMarker.remove();
          markersMap.current.delete(markerKey);
        }
        
        // Create new marker with optimized popup content
        console.log('LiveTracking: Creating new marker for:', username, 'at', [latitude, longitude], 'with key:', markerKey);
        const popupContent = `<div style="min-width:220px; max-width:320px; padding:16px 12px 12px 12px; border-radius:16px; box-shadow:0 4px 16px rgba(0,0,0,0.10); background:#fff; display:flex; flex-direction:column; align-items:flex-start; font-family:sans-serif;"><div style="display:flex; align-items:center; gap:12px; margin-bottom:8px;"><div style="width:40px; height:40px; border-radius:50%; background:${getUserGroupColor(user_group)}; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:20px; box-shadow:0 2px 6px rgba(0,0,0,0.10);">${username.charAt(0).toUpperCase()}</div><div><div style="font-size:1.1em; font-weight:700; color:#222; margin-bottom:2px;">${username}</div><span style="display:inline-block; background:${getUserGroupColor(user_group)}10; color:${getUserGroupColor(user_group)}; font-size:0.95em; font-weight:600; border-radius:999px; padding:2px 10px; margin-bottom:2px;">${user_group}</span></div></div><div style="margin-bottom:4px; font-size:1em; color:#444;"><b>ETA:</b> ${eta}</div></div>`;
        
        const customIcon = L.divIcon({
          className: 'custom-icon',
          html: createCustomIcon(username, user_group),
          iconSize: [40, 40],
          iconAnchor: [20, 20],
        });
        
        const marker = L.marker([latitude, longitude], { icon: customIcon })
          .addTo(leafletMap.current!)
          .bindPopup(popupContent, {
            offset: [0, 36],
            autoPan: true,
            autoPanPadding: [32, 32],
            closeButton: true,
            className: 'user-info-popup'
          });
        
        markersMap.current.set(markerKey, marker);
        console.log('LiveTracking: Successfully created marker for:', username);
      });
      
      // Process next batch if there are more locations
      if (endIndex < locationsToProcess.length) {
        setTimeout(() => processMarkerBatch(endIndex), 10); // 10ms delay between batches
      } else {
        // All batches processed, now remove old markers with smooth fade-out
        console.log('LiveTracking: Removing old markers. Current markers:', markersMap.current.size);
        const markersToRemove: { marker: L.Marker; key: string }[] = [];
        markersMap.current.forEach((marker, markerKey) => {
          if (!currentMarkerKeys.has(markerKey)) {
            console.log('LiveTracking: Preparing to remove old marker:', markerKey);
            markersToRemove.push({ marker, key: markerKey });
          }
        });
        
        // Apply smooth fade-out animation to each marker being removed
        markersToRemove.forEach(({ marker, key }, index) => {
          const iconElement = marker.getElement();
          if (iconElement) {
            // Add fade-out CSS transition
            iconElement.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';
            iconElement.style.opacity = '0';
            iconElement.style.transform = 'scale(0.8)';
            
            // Remove marker after animation completes
            setTimeout(() => {
              console.log('LiveTracking: Removing old marker after fade-out:', key);
              marker.remove();
              markersMap.current.delete(key);
            }, 300 + (index * 20)); // Stagger removal by 20ms for multiple markers
          } else {
            // Fallback: remove immediately if no icon element
            console.log('LiveTracking: Removing old marker immediately (no icon element):', key);
            marker.remove();
            markersMap.current.delete(key);
          }
        });
        
        console.log('LiveTracking: Marker update completed. Total markers:', markersMap.current.size);
      }
    };
    
    // Start processing the first batch
    if (locationsToProcess.length > 0) {
      processMarkerBatch(0);
    } else {
      // No locations to process, force clean up ONLY user markers from the map
      console.log('LiveTracking: No locations to process, force cleaning up user markers only');
      
      // Remove all markers from the user markers layer only
      if (markersLayer.current) {
        markersLayer.current.clearLayers();
        console.log('LiveTracking: Cleared all layers from markersLayer');
      }
      
      // Also clear the user markers map only
      markersMap.current.forEach((marker, markerKey) => {
        console.log('LiveTracking: Force removing user marker:', markerKey);
        try {
          marker.remove();
        } catch (error) {
          console.log('LiveTracking: Error removing user marker (may already be removed):', error);
        }
      });
      markersMap.current.clear();
      
      // Note: Emergency markers are preserved and managed separately
      console.log('LiveTracking: Emergency markers preserved during user marker cleanup');
    }
    
  }, [filteredLocations]);

  // After all markers are added, log the number of layers
  useEffect(() => {
    if (markersLayer.current) {
      console.log('LiveTracking: Number of markers in markersLayer:', markersLayer.current.getLayers().length);
    }
  }, [filteredLocations]);

  // Cleanup markers when sharing stops - with immediate removal to prevent glitch
  useEffect(() => {
    if (!isSharing) {
      // Immediate cleanup to prevent glitch - no delay needed
        const cleanupUserMarkers = async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            console.log('LiveTracking: Cleaning up user markers after sharing stopped for user:', user.id);
            
            // Get deviceId for this browser
            let deviceId = localStorage.getItem('onemove_device_id');
            if (!deviceId) {
              deviceId = 'web-' + crypto.randomUUID();
              localStorage.setItem('onemove_device_id', deviceId);
            }
            
            // Immediately remove user's own markers without animation to prevent glitch
            const markersToRemove: { marker: L.Marker; key: string }[] = [];
            markersMap.current.forEach((marker, markerKey) => {
              if (markerKey.includes(user.id) && markerKey.includes(deviceId)) {
                console.log('LiveTracking: Immediately removing user marker for current device:', markerKey);
                markersToRemove.push({ marker, key: markerKey });
              }
            });
            
            // Remove markers immediately to prevent glitch
            markersToRemove.forEach(({ marker, key }) => {
              console.log('LiveTracking: Removing user marker immediately:', key);
                  marker.remove();
                  markersMap.current.delete(key);
            });
            
            // Also remove only the current device from current shared locations state immediately
            setSharedLocations(prevLocations => 
              prevLocations.filter(location => 
                !(location.user_id === user.id && location.platform === 'web' && location.device_id === deviceId)
              )
            );
          }
        } catch (error) {
          console.error('Error cleaning up user markers:', error);
        }
      };
      
      cleanupUserMarkers();
    }
  }, [isSharing]);

  // Force cleanup when no locations are returned from database
  useEffect(() => {
    if (filteredLocations.length === 0 && markersMap.current.size > 0) {
      console.log('LiveTracking: Force cleanup triggered - no filtered locations but user markers exist');
      
      // Force remove only user markers from the map
      if (markersLayer.current) {
        markersLayer.current.clearLayers();
        console.log('LiveTracking: Force cleared all layers from markersLayer');
      }
      
      // Clear only the user markers map
      markersMap.current.forEach((marker, markerKey) => {
        console.log('LiveTracking: Force removing user marker from map:', markerKey);
        try {
          marker.remove();
        } catch (error) {
          console.log('LiveTracking: Error removing user marker (may already be removed):', error);
        }
      });
      markersMap.current.clear();
      
      // Note: Emergency markers are preserved and managed separately
      console.log('LiveTracking: Emergency markers preserved during filtered location cleanup');
    }
  }, [filteredLocations]);

  // Custom zoom handlers
  const handleZoomIn = () => {
    if (leafletMap.current) leafletMap.current.zoomIn();
  };
  const handleZoomOut = () => {
    if (leafletMap.current) leafletMap.current.zoomOut();
  };

  // Robust, free, CORS-friendly tile providers
  const tileLayers: Record<string, { url: string; attribution: string; subdomains?: string[] }> = {
    map: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '© OpenStreetMap contributors',
      subdomains: ['a', 'b', 'c'],
    },
    satellite: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attribution: 'Tiles © Esri',
      subdomains: [], // No subdomains for this provider
    },
    hybrid: {
      url: 'https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}.jpg',
      attribution: '© CNES, Distribution Airbus DS, © Airbus DS, © PlanetObserver (Contains Copernicus Data) | © Stadia Maps © OpenMapTiles © OpenStreetMap',
      subdomains: [],
    },
  };

  // Store all current tile layers for easy removal
  const tileLayerObjs = useRef<L.TileLayer[]>([]);

  // Robust tile layer switching
  useEffect(() => {
    if (!leafletMap.current) return;
    
    // Remove all previous tile layers
    tileLayerObjs.current.forEach(layer => {
      if (layer) {
        leafletMap.current!.removeLayer(layer);
      }
    });
    tileLayerObjs.current = [];
    
    try {
      if (mapStyle === 'hybrid') {
        // Use Stadia Maps Alidade Satellite for hybrid
        const base = L.tileLayer(tileLayers.hybrid.url, {
          attribution: tileLayers.hybrid.attribution,
          subdomains: tileLayers.hybrid.subdomains || [],
        });
        base.addTo(leafletMap.current);
        tileLayerObjs.current.push(base);
      } else {
        // Add single base layer
        const layerConfig = tileLayers[mapStyle] || tileLayers['map'];
        const base = L.tileLayer(layerConfig.url, {
          attribution: layerConfig.attribution,
          subdomains: layerConfig.subdomains || [],
        });
        
        base.on('tileerror', (err) => {
          console.error('Tile layer error:', err?.type || err?.error || err);
          // Fallback to OSM
          tileLayerObjs.current.forEach(l => {
            if (l) {
              leafletMap.current!.removeLayer(l);
            }
          });
          tileLayerObjs.current = [];
          const fallback = L.tileLayer(tileLayers['map'].url, {
            attribution: tileLayers['map'].attribution,
            subdomains: tileLayers['map'].subdomains || [],
          });
          fallback.addTo(leafletMap.current!);
          tileLayerObjs.current.push(fallback);
        });
        
        base.addTo(leafletMap.current);
        tileLayerObjs.current.push(base);
      }
    } catch (e) {
      console.error('Error adding tile layer:', JSON.stringify(e, null, 2));
      // Fallback to OSM
      const fallback = L.tileLayer(tileLayers['map'].url, {
        attribution: tileLayers['map'].attribution,
        subdomains: tileLayers['map'].subdomains || [],
      });
      fallback.addTo(leafletMap.current!);
      tileLayerObjs.current.push(fallback);
    }
  }, [mapStyle]);

  // Fetch active emergencies
  const fetchActiveEmergencies = async () => {
    try {
      const { data, error } = await supabase
        .from('emergency_alerts')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching emergencies:', JSON.stringify(error, null, 2));
      } else if (data) {
        setActiveEmergencies(data as EmergencyAlert[]);
      }
    } catch (error) {
      console.error('Error fetching emergencies:', JSON.stringify(error, null, 2));
    }
  };

  // Create emergency marker
  const createEmergencyMarker = (alert: EmergencyAlert) => {
    if (!leafletMap.current || !emergencyMarkersLayer.current) return;

    const emergencyIcon = L.divIcon({
      className: 'emergency-icon',
      html: `
        <div style="
          width: 40px; 
          height: 40px; 
          background: #ff4444; 
          border: 3px solid white; 
          border-radius: 50%; 
          display: flex; 
          align-items: center; 
          justify-content: center;
          color: white; 
          font-weight: bold;
          font-size: 18px; 
          box-shadow: 0 2px 8px rgba(255,68,68,0.4);
          animation: pulse 2s infinite;
        ">
          ⚠
        </div>
        <style>
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
        </style>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });

    const popupContent = `
      <div style='min-width:200px'>
        <b style="color: #ff4444;">EMERGENCY ALERT</b><br/>
        <b>${alert.username}</b><br/>
        <small>${alert.user_group}</small><br/>
        <small>${alert.address}</small><br/>
        ${alert.notes ? `<small>Notes: ${alert.notes}</small>` : ''}
      </div>
    `;

    const marker = L.marker([alert.latitude, alert.longitude], { icon: emergencyIcon })
      .addTo(emergencyMarkersLayer.current)
      .bindPopup(popupContent)
      .on('click', () => {
      setSelectedEmergency(alert);
        setEmergencyDialogOpen(true);
    });

    // Store the marker reference for proper cleanup
    emergencyMarkersMap.current.set(alert.id, marker);
    
    console.log('LiveTracking: Created emergency marker for:', alert.username, 'at', [alert.latitude, alert.longitude]);
  };

  // Update emergency markers
  const updateEmergencyMarkers = () => {
    if (!emergencyMarkersLayer.current) return;
    
    console.log('LiveTracking: Updating emergency markers for', activeEmergencies.length, 'emergencies');
    
    // Clear existing emergency markers only
    emergencyMarkersLayer.current.clearLayers();
    emergencyMarkersMap.current.clear();
    
    // Add new emergency markers
    activeEmergencies.forEach(createEmergencyMarker);
    
    console.log('LiveTracking: Emergency markers updated. Total emergency markers:', emergencyMarkersMap.current.size);
  };

  useEffect(() => {
    fetchActiveEmergencies();
    const interval = setInterval(fetchActiveEmergencies, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    updateEmergencyMarkers();
  }, [activeEmergencies]);

  // Layer menu handlers
  const handleLayerMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleLayerMenuClose = () => {
    setAnchorEl(null);
  };
  const handleLayerSelect = (style: 'map' | 'satellite' | 'hybrid') => {
    setMapStyle(style);
    setAnchorEl(null);
  };

  // State for minimizing legend and user table
  const [legendMinimized, setLegendMinimized] = useState(true);
  // State for user search
  const [userSearch, setUserSearch] = useState('');
  // State for minimizing the filter box
  const [filterBoxMinimized, setFilterBoxMinimized] = useState(true);
  // State for live user panel
  const [sidebarMinimized, setSidebarMinimized] = useState(true);

  return (
    <>
      <Box sx={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', overflow: 'hidden', background: '#f5f5f5' }}>
        {/* Map as background */}
        <Box
          id="map"
          ref={mapRef}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1,
          }}
        />

        {/* Background tracking notification */}
        {isSharing && (
          <Box
            sx={{
              position: 'fixed',
              top: 60, // Shifted further down for better visibility
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: isBackgroundTracking ? 'rgba(76, 175, 80, 0.95)' : 'rgba(255, 152, 0, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              boxShadow: isBackgroundTracking ? '0 4px 16px rgba(76, 175, 80, 0.3)' : '0 4px 16px rgba(255, 152, 0, 0.3)',
              border: isBackgroundTracking ? '1px solid rgba(76, 175, 80, 0.2)' : '1px solid rgba(255, 152, 0, 0.2)',
              zIndex: 1001,
              px: 3,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              maxWidth: '90vw',
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: '#fff',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 },
                },
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.875rem' }}>
                {isBackgroundTracking ? '🔄 Background Tracking Active!' : '📍 Location Sharing Active'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9, fontSize: '0.75rem' }}>
                {isBackgroundTracking 
                  ? 'Location updates continue even when you navigate away or minimize the browser'
                  : 'Stay on this page to continue tracking your location'
                }
              </Typography>
            </Box>
          </Box>
        )}

        {/* Floating, compact user list panel (Life360 style) - now opens from top left, even higher */}
        {!sidebarMinimized && (
          <Box
            sx={{
              position: 'fixed',
              left: 24,
              top: 80,
              zIndex: 1200,
              width: 320,
              minHeight: 520,
              maxHeight: 'calc(100vh - 180px)', // Leaves space for bottom taskbar
              background: 'rgba(255,255,255,0.98)',
              borderRadius: 4,
              boxShadow: 6,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              overflow: 'hidden',
              marginBottom: '32px', // Extra space above taskbar
              transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ color: '#333' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>Live Users</Typography>
            </Box>
              <IconButton size="small" onClick={() => { setSidebarMinimized(true); setFilterBoxMinimized(true); }}>
                <span style={{ fontSize: 18 }}>&#x25B2;</span>
              </IconButton>
          </Box>
            {/* User-friendly group filter with Select All and vertical layout, now minimizable */}
            <Box sx={{ mb: 1, background: '#f8f9fa', borderRadius: 2, p: 2, boxShadow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 2, background: '#f8f9fa' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#333', mb: 1, display: 'block' }}>
                  Filter by Group
                </Typography>
                <IconButton size="small" onClick={() => setFilterBoxMinimized(!filterBoxMinimized)}>
                  <span style={{ fontSize: 16 }}>{filterBoxMinimized ? '▼' : '▲'}</span>
                </IconButton>
            </Box>
              {!filterBoxMinimized && (
                <Box sx={{ maxHeight: 240, overflowY: 'auto', pr: 1 }}>
                  <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                          checked={Object.values(selectedGroups).every(Boolean)}
                          indeterminate={Object.values(selectedGroups).some(Boolean) && !Object.values(selectedGroups).every(Boolean)}
                          onChange={(_, checked) => {
                            const newGroups: { [key: string]: boolean } = {};
                            USER_GROUPS.forEach(group => {
                              newGroups[group] = checked;
                            });
                            setSelectedGroups(newGroups);
                          }}
                          sx={{ transform: 'scale(1.2)' }}
                        />
                      }
                      label={<b>Select All</b>}
                      sx={{ mb: 1 }}
                    />
                    <Divider sx={{ mb: 1 }} />
                    {USER_GROUPS.map(group => (
                  <FormControlLabel
                          key={group}
                    control={
                      <Checkbox
                              checked={Boolean((selectedGroups as Record<string, boolean>)[group])}
                              onChange={e => setSelectedGroups({ ...selectedGroups, [group]: e.target.checked })}
                              size="medium"
                              sx={{ transform: 'scale(1.2)' }}
                            />
                      }
                      label={<Typography variant="body2">{group}</Typography>}
                      sx={{ mb: 0.5, ml: 1 }}
                    />
                  ))}
            </FormGroup>
          </Box>
            )}
        </Box>
          <TextField
            placeholder="Search user..."
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
                size="small"
            sx={{ mb: 1, background: '#fff', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
      <Box sx={{ 
            flex: 1,
            maxHeight: 'calc(100vh - 350px)', // Responsive to viewport and taskbar
            overflowY: 'auto',
            pr: 1,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-thumb': { background: '#e0e0e0', borderRadius: 4 }
          }}>
            {filteredLocations
              .filter(loc =>
                (selectedGroups[loc.user_group] ?? false) &&
                (loc.username.toLowerCase().includes(userSearch.toLowerCase()) ||
                 loc.user_group.toLowerCase().includes(userSearch.toLowerCase()))
              )
              .map(loc => (
                <Box key={loc.sharing_id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <UserIcon username={loc.username} size={48} backgroundColor={getUserGroupColor(loc.user_group)} />
                  <Box>
                    <Typography fontWeight={600}>{loc.username}</Typography>
                    <Chip label={loc.user_group} size="small" sx={{ mt: 0.5, mb: 0.5 }} />
                    <Typography variant="caption" color="text.secondary">
                      {loc.eta} • {loc.platform}
                    </Typography>
      </Box>
      </Box>
              ))}
            {filteredLocations.filter(loc => (selectedGroups[loc.user_group] ?? false)).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No users found
              </Typography>
            )}
          </Box>
        </Box>
      )}
      {/* Minimized floating button for user list panel at top left, even higher */}
      {sidebarMinimized && (
        <Tooltip title="Show Live Users" placement="right">
          <IconButton
          sx={{
              position: 'fixed',
              top: 120,
              left: 16,
              zIndex: 3000,
              background: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              borderRadius: '50%',
              border: '2px solid #e0e0e0',
              width: 48,
              height: 48,
              p: 0,
              '&:hover': { background: '#f5f5f5' },
            }}
            onClick={() => setSidebarMinimized(false)}
          >
            <PersonIcon sx={{ color: '#333', fontSize: 32 }} />
          </IconButton>
        </Tooltip>
      )}
      
      {/* Floating action buttons (bottom center) */}
      <Box
        className="live-action-buttons"
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: 32,
          transform: 'translateX(-50%)',
          zIndex: 10,
          background: 'white',
          borderRadius: 3,
          boxShadow: 6,
          p: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
        }}
      >
              <Button
                variant="contained"
                color={isSharing ? 'error' : 'primary'}
                startIcon={isSharing ? <StopCircleIcon /> : <ShareLocationIcon />}
                onClick={isSharing ? stopSharing : startSharing}
                disabled={false}
                sx={{ 
            minWidth: 200,
            height: 60,
                  fontWeight: 700, 
            fontSize: 13,
            borderRadius: 3,
            py: 0.5,
            px: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            lineHeight: 1.1,
            letterSpacing: 0.5,
            background: isSharing
              ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)'
              : 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
            boxShadow: isSharing
              ? '0 4px 24px 0 rgba(244,67,54,0.10)'
              : '0 4px 24px 0 rgba(33,150,243,0.10)',
            transition: 'all 0.18s cubic-bezier(.4,0,.2,1)',
                  '&:hover': {
              transform: 'scale(1.04)',
              boxShadow: isSharing
                ? '0 8px 32px 0 rgba(244,67,54,0.18)'
                : '0 8px 32px 0 rgba(33,150,243,0.18)',
              filter: 'brightness(1.05)',
                  },
                  '&:active': {
              transform: 'scale(0.98)',
              boxShadow: 2,
                  },
                  '&:disabled': {
                    opacity: 0.7,
                    transform: 'none',
                    boxShadow: 1,
            },
                }}
              >
                <>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>SHARE LOCATION</span>
                  {isSharing && (
                    <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.8, fontSize: '0.75rem' }}>
                      Persistent tracking active
                    </Typography>
                  )}
                </>
              </Button>
          <Button 
                variant="contained"
                color="primary"
          sx={{
            minWidth: 200,
            height: 72,
            fontWeight: 700,
            fontSize: 17,
            borderRadius: 3,
            py: 1.5,
            px: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            lineHeight: 1.1,
            letterSpacing: 0.5,
            background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
            boxShadow: '0 4px 24px 0 rgba(33,150,243,0.10)',
            transition: 'all 0.18s cubic-bezier(.4,0,.2,1)',
            '&:hover': {
              transform: 'scale(1.04)',
              boxShadow: '0 8px 32px 0 rgba(33,150,243,0.18)',
              filter: 'brightness(1.05)',
            },
            '&:active': {
              transform: 'scale(0.98)',
              boxShadow: 2,
            },
            '&:disabled': {
              opacity: 0.7,
              transform: 'none',
              boxShadow: 1,
            },
          }}
          onClick={() => navigate('/playback')}
              >
          <span style={{ fontWeight: 700, fontSize: 17 }}>PLAYBACK</span>
          </Button>
        <EmergencyAlertTrigger
          variant="contained"
          sx={{
            minWidth: 200,
            height: 72,
            fontWeight: 700,
            fontSize: 17,
            borderRadius: 3,
            py: 1.5,
            px: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            lineHeight: 1.1,
            letterSpacing: 0.5,
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
            boxShadow: '0 4px 24px 0 rgba(244,67,54,0.10)',
            transition: 'all 0.18s cubic-bezier(.4,0,.2,1)',
            '&:hover': {
              transform: 'scale(1.04)',
              boxShadow: '0 8px 32px 0 rgba(244,67,54,0.18)',
              filter: 'brightness(1.05)',
            },
            '&:active': {
              transform: 'scale(0.98)',
              boxShadow: 2,
            },
            '&:disabled': {
              opacity: 0.7,
              transform: 'none',
              boxShadow: 1,
            },
          }}
        />
        </Box>
          
      {/* Right-side vertical toolbar with zoom in, zoom out, map layer button, and legend below */}
      <Box
        sx={{
          position: 'absolute',
          right: 16,
          top: 100,
          zIndex: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Tooltip title="Zoom In">
          <IconButton onClick={handleZoomIn} sx={{ background: '#fff', boxShadow: 2, mb: 1 }}>
            <AddIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <IconButton onClick={handleZoomOut} sx={{ background: '#fff', boxShadow: 2, mb: 1 }}>
            <RemoveIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Map Layers">
          <IconButton onClick={handleLayerMenuClick} sx={{ background: '#fff', boxShadow: 2, mb: 1 }}>
            <LayersIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleLayerMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem selected={mapStyle === 'map'} onClick={() => handleLayerSelect('map')}>Default Map</MenuItem>
          <MenuItem selected={mapStyle === 'satellite'} onClick={() => handleLayerSelect('satellite')}>Satellite</MenuItem>
          <MenuItem selected={mapStyle === 'hybrid'} onClick={() => handleLayerSelect('hybrid')}>Hybrid</MenuItem>
        </Menu>
      </Box>

        {/* User Group Legend below the buttons */}
        {!legendMinimized ? (
          <Box
            sx={{ 
              position: 'fixed',
              left: 16,
              bottom: 32,
              top: 'auto',
              mt: 0,
              background: 'rgba(255,255,255,0.98)',
              borderRadius: 3,
              boxShadow: 2,
              border: '1px solid #e0e0e0',
              p: 2,
              minWidth: 180,
              maxWidth: 260,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 1.2,
              zIndex: 1202,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1em', letterSpacing: 0.2 }}>
                Groups
          </Typography>
              <IconButton size="small" onClick={() => setLegendMinimized(true)} sx={{ ml: 1, p: 0 }}>
                <span style={{ fontSize: 16 }}>–</span>
              </IconButton>
          </Box>
            {USER_GROUPS.map((group) => (
              <Box key={group} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.5 }}>
                <Box
                  sx={{ 
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: getUserGroupColor(group),
                    border: '2px solid #fff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                    flexShrink: 0,
                    mr: 1,
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: '1em', color: '#444', fontWeight: 500 }}>
                  {group}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
        <Box
          sx={{
              position: 'fixed',
              left: 16,
              bottom: 32,
              top: 'auto',
              mt: 0,
              background: 'rgba(255,255,255,0.85)',
              borderRadius: '50%',
              boxShadow: 2,
              width: 32,
              height: 32,
            display: 'flex',
            alignItems: 'center',
              justifyContent: 'center',
            cursor: 'pointer',
              border: '1px solid #e0e0e0',
              zIndex: 1202,
            }}
            onClick={() => setLegendMinimized(false)}
            title="Show User Group Legend"
          >
            <span style={{ fontSize: 20, color: '#333' }}>≡</span>
        </Box>
      )}

        {/* Connection Status Indicator */}
        <Box
        sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            background: realtimeStatus === 'connected' ? 'rgba(40, 167, 69, 0.9)' : 
                        realtimeStatus === 'connecting' ? 'rgba(255, 193, 7, 0.9)' : 
                        'rgba(220, 53, 69, 0.9)',
            color: 'white',
            borderRadius: 2,
            px: 2,
            py: 1,
            fontSize: '0.8rem',
            fontWeight: 600,
          display: 'flex',
            alignItems: 'center',
            gap: 1,
            zIndex: 1203,
            boxShadow: 2,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: realtimeStatus === 'connected' ? '#fff' : 
                              realtimeStatus === 'connecting' ? '#fff' : '#fff',
              animation: realtimeStatus === 'connecting' ? 'pulse 2s infinite' : 'none',
            }}
          />
          {realtimeStatus === 'connected' && 'Real-time Active'}
          {realtimeStatus === 'connecting' && 'Connecting...'}
          {realtimeStatus === 'disconnected' && (isPollingActive ? 'Polling Fallback' : 'Disconnected')}
          </Box>


      </Box>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Filter by User Group</DialogTitle>
        <DialogContent>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Object.values(selectedGroups).every(Boolean)}
                  indeterminate={Object.values(selectedGroups).some(Boolean) && !Object.values(selectedGroups).every(Boolean)}
                  onChange={(_, checked) => {
                    const newGroups: { [key: string]: boolean } = {};
                    USER_GROUPS.forEach(group => {
                      newGroups[group] = checked;
                    });
                    setSelectedGroups(newGroups);
                  }}
                  sx={{ transform: 'scale(1.2)' }}
                />
              }
              label={<b>Select All</b>}
              sx={{ mb: 1 }}
            />
            <Box sx={{ borderBottom: '1px solid #eee', mb: 2 }} />
            {USER_GROUPS.map((group) => (
              <FormControlLabel
                key={group}
                control={
                  <Checkbox
                    checked={!!selectedGroups[group as string]}
                    onChange={e => setSelectedGroups({ ...selectedGroups, [group]: e.target.checked })}
                    name={group}
                    sx={{ transform: 'scale(1.2)' }}
                  />
                }
                label={group}
                sx={{ mb: 1, ml: 1 }}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Emergency Alert Dialog */}
      <Dialog open={emergencyDialogOpen} onClose={() => setEmergencyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#ff4444' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="error" />
            Emergency Alert
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEmergency ? (
            <List>
              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={selectedEmergency.username}
                  secondary={selectedEmergency.user_group}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PlaceIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Location"
                  secondary={selectedEmergency.address}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AccessTimeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Time"
                  secondary={new Date(selectedEmergency.created_at).toLocaleString()}
                />
              </ListItem>
              {selectedEmergency.notes && (
                <ListItem>
                  <ListItemIcon>
                    <NotesIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Notes"
                    secondary={selectedEmergency.notes}
                  />
                </ListItem>
              )}
            </List>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmergencyDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <TidioChat />
    </>
  );
};

const LiveTracking = () => (
  <LocationSharingProvider>
    <LiveTrackingContent />
  </LocationSharingProvider>
);

export default LiveTracking; 